/**
 * The region switcher in the header, beside the language pills.
 *
 * **Switching region is a navigation, not a state change.** Regions are separate sites -
 * separate domains, separate pages, separate slugs - so this is a menu of links, and it
 * uses `<a>` rather than the router: the target is usually a different origin, and even
 * where it is not, the whole app has to reload against the new region's content. Where
 * each link points is `regionSwitchUrl`'s job, and it has three cases; the comment there
 * is the one worth reading.
 *
 * It lands on the target region's default language at the site root, never on the
 * equivalent of the current page. There is no equivalent: slugs belong to a region and
 * are not translations of one another, so `/pl/podroze-sluzbowe` has no counterpart to
 * carry across, and guessing one produces a 404 that looks like the switcher is broken.
 *
 * A menu rather than a `<select>`: a select whose options navigate is awkward for a
 * keyboard (choosing with the arrow keys fires a change per option), and this one has to
 * carry a "you are here" mark.
 */
import { useEffect, useRef, useState } from 'react';
import { Icon } from './ui/Icon';
import { useSite } from '../lib/site-context';
import { regionSwitchUrl } from '../lib/regions';

export function RegionSwitcher({ className = '' }: { className?: string }) {
  const { region, regions, t } = useSite();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // A menu that stays open after a click elsewhere, or after Escape, is a menu that gets
  // in the way of the page behind it.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // One region is not a choice. The fallback table has four, so this only happens when
  // the CMS genuinely publishes one.
  if (regions.length < 2) return null;

  return (
    <div ref={box} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('a11y.chooseRegion')}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-caption uppercase text-ink-faint transition hover:text-ink"
      >
        {region.code}
        <Icon name="chevron-down" size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('nav.region')}
          className="absolute right-0 top-full z-40 mt-2 min-w-[200px] overflow-hidden rounded-m border border-grey-300 bg-surface py-2 shadow-lg"
        >
          {regions.map((r) => (
            <a
              key={r.code}
              role="menuitem"
              href={regionSwitchUrl(regions, r, window.location)}
              aria-current={r.code === region.code ? 'true' : undefined}
              className={`flex items-center justify-between gap-4 px-5 py-2.5 text-body-sm transition ${
                r.code === region.code ? 'bg-surface-muted text-ink' : 'text-ink-muted hover:bg-surface-muted hover:text-ink'
              }`}
            >
              {r.name}
              <span className="text-caption uppercase text-ink-faint">{r.code}</span>
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
